import {
  Await,
  LoaderFunctionArgs,
  Outlet,
  redirect,
  useLoaderData,
  useParams,
} from 'react-router-dom'
import React, { Suspense, useMemo } from 'react'
import { VpButton, VpIcon } from '@vtmn-play/react'
import DiagTopBar from '@/src/components/diagnoses/diag-top-bar/DiagTopBar'
import { useTranslation } from 'react-i18next'
import { AnswerType } from '@/domains/AnswerType'
import { QuestionSkeleton } from '@/src/components/layout/question/QuestionSkeleton'
import useQuestionAnswers from '@/src/hooks/question-layout/use-question-answers/useQuestionAnswers'
import useQuestionLayoutNavigation from '@/src/hooks/question-layout/use-question-layout-redirect/useQuestionLayoutNavigation'
import { DiagnosisDetailsDto, DiagnosisStatus } from '@/infra/api/generated/model'
import { getDiagnoses } from '@/infra/api/generated/diagnoses/diagnoses'
import { trackEvent } from '@/src/analytics/amplitude'

const { getDiagnosisDetails } = getDiagnoses()
export const Loader = async ({ params }: LoaderFunctionArgs) => {
  const { diagnosisId } = params
  const diagnosisDetails = await getDiagnosisDetails(diagnosisId!)
  if (diagnosisDetails.status !== DiagnosisStatus.IN_PROGRESS) {
    return redirect('/my-hub/diagnosis')
  }
  return {
    diagnosisDetails,
  }
}

const RootLayout = () => {
  const { diagnosisDetails } = useLoaderData()

  return (
    <Suspense fallback={<QuestionSkeleton />}>
      <Await resolve={diagnosisDetails}>
        {(resolved) => <RootLayoutContent data={resolved} />}
      </Await>
    </Suspense>
  )
}

const RootLayoutContent = ({ data }: { data: DiagnosisDetailsDto }) => {
  const { t } = useTranslation('common')
  const { diagnosisId, order } = useParams()
  const { questions, family } = data

  const { answers, updateAnswer } = useQuestionAnswers(questions)
  const { goToQuestion } = useQuestionLayoutNavigation(data)
  const total = questions.length
  const idx = useMemo(
    () => questions.findIndex((q) => q.order.toString() === order),
    [order, questions]
  )
  const currentQuestion = questions[idx]

  // Show loading skeleton if current question not ready
  if (!currentQuestion) {
    return <QuestionSkeleton />
  }

  const handleQuestionAnswer = (questionId: string, answer: AnswerType) => {
    updateAnswer(questionId, answer)
    if (idx + 1 < questions.length) goToQuestion(idx + 1)
  }

  const handleBackButtonClick = () => {
    trackEvent('QUESTION_BACK_BUTTON_CLICK', {
      familyId: family.id,
      questionId: currentQuestion.questionId,
    })
    goToQuestion(idx - 1)
  }

  const previousButton = idx > 0 && (
    <VpButton
      variant="tertiary"
      startSlot={<VpIcon name="chevron-left" />}
      onClick={handleBackButtonClick}
    >
      {t('pages.questions.previous')}
    </VpButton>
  )

  const nextButton = idx < questions.length - 1 && (
    <VpButton
      variant="tertiary"
      endSlot={<VpIcon name="chevron-right" />}
      disabled={answers[currentQuestion.questionId] == null}
      onClick={() => goToQuestion(idx + 1)}
    >
      {t('pages.questions.next')}
    </VpButton>
  )

  // =====================
  // Render layout
  // =====================
  return (
    <div className="h-dvh flex flex-col">
      <DiagTopBar currentIndex={idx + 1} total={total} />
      <main className="flex-1 overflow-y-auto">
        <Outlet
          key={currentQuestion.questionId}
          context={{
            question: currentQuestion,
            currentAnswer: answers[currentQuestion.questionId],
            family,
            diagnosisId,
            handleQuestionAnswer,
          }}
        />
      </main>
      <footer className="flex justify-between p-4">
        <div>{previousButton}</div>
        <div>{nextButton}</div>
      </footer>
    </div>
  )
}

export default RootLayout
